import type { AnalyticsHost } from '../api';
import type { HostSelection } from '../filters';
import type { TickState } from '../selection';
import { hostGroups, hostKindLabel } from './hosts';
import type { QualityEnvelope, QualityExposure, QualityHostCatalog, QualityHostRow } from './types';

export type AddressRow = { host: string; kindLabel: 'Website' | 'Landing pages'; tick: TickState };

/** One group of host rows; `exposure` is null for the one ungrouped list shown before any answer. */
export type AddressGroup = { exposure: QualityExposure | null; tick: TickState; rows: AddressRow[] };

/**
 * The Filters sheet's Addresses section. `shown` when the brand has more than one kept host or its default is empty;
 * `unavailable` while the address list cannot be read (note 4's text, no rows); `rail` is the section's value.
 */
export type AddressSection = { shown: boolean; unavailable: boolean; groups: AddressGroup[]; rail: string };

export const addressSection = (input: {
  selection: HostSelection;
  catalogue: readonly AnalyticsHost[];
  latest: QualityEnvelope | null;
}): AddressSection => {
  const { selection, catalogue, latest } = input;
  const notDefault = selection.kind !== 'default';
  if (latest?.hostCatalog.status === 'unavailable')
    return { shown: true, unavailable: true, groups: [], rail: 'Nothing selected' };

  const ticked = new Set(tickedHosts(selection, latest?.hosts ?? [], latest?.hostCatalog.defaultBasis ?? 'none'));
  const rowOf = (h: { host: string; kind: QualityHostRow['kind'] }): AddressRow => ({
    host: h.host,
    kindLabel: hostKindLabel(h),
    tick: ticked.has(h.host) ? 'on' : 'off',
  });
  const groupOf = (exposure: QualityExposure | null, rows: AddressRow[]): AddressGroup => ({
    exposure,
    tick: groupTick(rows),
    rows,
  });

  let groups: AddressGroup[];
  if (latest === null) {
    groups = catalogue.length ? [groupOf(null, catalogue.map(rowOf))] : [];
  } else {
    const order = new Map(catalogue.map((h, i) => [h.host, i]));
    const rank = (host: string): number => order.get(host) ?? catalogue.length;
    groups = hostGroups(latest.hosts).map((g) =>
      groupOf(g.exposure, [...g.hosts].sort((a, b) => rank(a.host) - rank(b.host)).map(rowOf)),
    );
  }

  const kept = groups.reduce((n, g) => n + g.rows.length, 0);
  const emptyDefault = latest?.hostCatalog.defaultBasis === 'none';
  const shownHosts = groups.flatMap((g) => g.rows).filter((r) => r.tick === 'on');
  let rail: string;
  // Before the first answer the default's hosts are not known yet, but the default is in effect.
  if (latest === null && selection.kind === 'default') rail = 'All public addresses';
  else if (shownHosts.length === 0) rail = 'Nothing selected';
  else if (selection.kind === 'default' && latest?.hostCatalog.defaultBasis === 'internal')
    rail = 'All internal addresses';
  else if (selection.kind === 'default') rail = 'All public addresses';
  else rail = shownHosts.map((r) => r.host).join(', ');
  return { shown: kept > 1 || emptyDefault || notDefault, unavailable: false, groups, rail };
};

const isKept = (row: QualityHostRow): boolean => row.reason === null || row.reason === 'not_in_registry';

type DefaultBasis = QualityHostCatalog['defaultBasis'];

/**
 * The hosts a choice ticks. The default ticks the kept public hosts, or the internal ones when the default is internal,
 * and none when it is empty, as Analytics defines it; never the answer's `selected` flags, which mark the selection the
 * answer counted (the owner's own choice when one was sent).
 */
const tickedHosts = (selection: HostSelection, rows: readonly QualityHostRow[], basis: DefaultBasis): string[] => {
  if (selection.kind === 'none') return [];
  if (selection.kind === 'some') return selection.hosts;
  if (basis === 'none') return [];
  const exposure: QualityExposure = basis === 'internal' ? 'internal' : 'public';
  return rows.filter((r) => isKept(r) && r.exposure === exposure).map((r) => r.host);
};

const groupTick = (rows: readonly AddressRow[]): TickState => {
  const on = rows.filter((r) => r.tick === 'on').length;
  return on === 0 ? 'off' : on === rows.length ? 'on' : 'mixed';
};

export type AddressTarget = { group: QualityExposure } | { host: string };

/** The choice after a click on a group or a host, over the latest answer's rows and its `hostCatalog.defaultBasis`. */
export const toggleAddress = (
  selection: HostSelection,
  target: AddressTarget,
  rows: readonly QualityHostRow[],
  basis: DefaultBasis,
): HostSelection => {
  const ticked = new Set(tickedHosts(selection, rows, basis));
  if ('host' in target) {
    if (ticked.has(target.host)) ticked.delete(target.host);
    else ticked.add(target.host);
  } else {
    const members = rows.filter((r) => isKept(r) && r.exposure === target.group).map((r) => r.host);
    const all = members.every((h) => ticked.has(h));
    members.forEach((h) => (all ? ticked.delete(h) : ticked.add(h)));
  }
  const ordered = rows.map((r) => r.host).filter((h) => ticked.has(h));
  const rest = [...ticked].filter((h) => !rows.some((r) => r.host === h));
  const hosts = [...ordered, ...rest];
  if (hosts.length === 0) return { kind: 'none' };
  const byDefault = tickedHosts({ kind: 'default' }, rows, basis);
  if (byDefault.length === hosts.length && byDefault.every((h) => ticked.has(h))) return { kind: 'default' };
  return { kind: 'some', hosts };
};
